import * as v from 'valibot'

// Valibot duplicate of `Profile` from the `shared` package. Used in the root
// route's `validateSearch` so that parsing URL search params doesn't pull the
// zod schema (and the entire zod runtime) into the entry chunk.
//
// Keep this file in sync with `apps/shared/src/shared.ts` (`Profile`,
// `Filters`, `SiteInferProfile`, `TeamProfileEvaluation`, `Site`).

const Site = v.picklist([
  'universal',
  'linkedin',
  'infojobs',
  'buscojobs',
  'manpower',
  'randstad',
  'tecnoempleo',
  'talentclue',
])

const Sex = v.picklist(['male', 'female', 'neutral'])

// Exported so the root search schema's `collectiveFilters` can pick its subset from here rather than
// re-typing the bounds a third time (zod, this file, __root.tsx). See `CollectiveFilters` in shared.ts,
// which picks the same keys off the zod `Filters`.
export const FiltersShape = {
  sex: v.fallback(v.optional(v.array(Sex)), undefined),
  ageMin: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(120))),
  ageMax: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(120))),
  peopleInCharge: v.optional(v.array(v.boolean())),
  level: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(5)))),
  function: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(34)))),
  sector: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(147)))),
  employees: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(8)))),
  educationalLevel: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(6)))),
  country: v.optional(v.array(v.string())),
  ids: v.optional(v.array(v.number())),
  filtersOrder: v.optional(v.array(v.string())),
} as const

const SiteInferProfileShape = {
  site: v.fallback(v.optional(Site), undefined),
  name: v.nullish(v.string()),
  levelText: v.optional(v.string()),
  functionText: v.optional(v.string()),
  sectorText: v.optional(v.string()),
  ids: v.optional(v.array(v.number())),
} as const

const TeamProfileEvaluationShape = {
  teamId: v.optional(v.number()),
  evaluation: v.fallback(v.optional(v.picklist(['owns', 'ownsManual', 'team', 'manual'])), undefined),
  members: v.optional(v.array(v.number())),
  name: v.nullish(v.string()),
} as const

// Mirrors `answerSchema` / `answersSchema` from `shared.ts`: a 36-element
// tuple where each element is one of the 6 permutations of `[0, 1, 2]`.
const answerSchema = v.union([
  v.tuple([v.literal(0), v.literal(1), v.literal(2)]),
  v.tuple([v.literal(0), v.literal(2), v.literal(1)]),
  v.tuple([v.literal(1), v.literal(0), v.literal(2)]),
  v.tuple([v.literal(1), v.literal(2), v.literal(0)]),
  v.tuple([v.literal(2), v.literal(1), v.literal(0)]),
  v.tuple([v.literal(2), v.literal(0), v.literal(1)]),
])

const answersSchema = v.tuple([
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
  answerSchema,
])

export const Profile = v.variant('type', [
  v.object({ type: v.literal('employee'), id: v.number() }),
  v.object({
    type: v.literal('market_company'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_company_job'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({ type: v.literal('applicant'), id: v.optional(v.number()) }),
  v.object({
    type: v.literal('job'),
    id: v.number(),
    source: v.fallback(v.optional(v.picklist(['described', 'integrants'])), undefined),
    ...SiteInferProfileShape,
  }),
  v.object({ type: v.literal('team'), id: v.number(), ...TeamProfileEvaluationShape }),
  v.object({ type: v.literal('member'), id: v.number(), ...TeamProfileEvaluationShape }),
  v.object({ type: v.literal('org_group'), id: v.string() }),
  v.object({ type: v.literal('org_user'), id: v.string() }),
  v.object({
    type: v.literal('market'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_applicant'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_job'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('public'),
    id: v.optional(v.number()),
    name: v.optional(v.string()),
    competences: v.optional(answersSchema),
    values: v.optional(answersSchema),
    personality: v.optional(answersSchema),
  }),
])

export type Profile = v.InferOutput<typeof Profile>
